// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@flip/App/Components/Common/XngZone";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@flip/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { DivWrapper } from "@flip/XDL/constants";
import { avoidBreakInsidePrintElement, hideElementOnPrint, xClassNames, xScreen } from "@flip/XDL/Utilities";
import color from "@flip/XDL/Utilities/color";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { Typography } from "@insite/mobius/index";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasShellContext & HasProductContext;

export interface ProductDetailsViewStyles {
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;

    // XNG-Changes
    container?: InjectableCss;
    leftSection?: InjectableCss;
    mainSection?: InjectableCss;
    partNumbersSection?: InjectableCss;
    rightSection?: InjectableCss;
    imagesSection?: InjectableCss;
    actionsWrapper?: InjectableCss;
    stickyWrapper?: InjectableCss;
    headerPage?: MobiusPageProps;
    productTitleHiddenMobile?: InjectableCss;
    productTitleHidden?: InjectableCss;
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
    bottomSection?: InjectableCss;
    tariffTextStyle?: TypographyPresentationProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = useState(false);
    const isShellEditingMood = props.shellContext?.isEditing;
    // XNG-Change
    const productHasMultipleSaleQty =
        parseInt((props.productContext.product?.detail?.multipleSaleQty || 0).toString(), 10) > 1;
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="productDetailsView">
                {isTabletScreen && (
                    <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details Title" />
                )}
                <StyledWrapper {...styles.imagesSection}>
                    <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                </StyledWrapper>
                <StyledWrapper {...styles.mainSection}>
                    {!isTabletScreen && (
                        <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details Title" />
                    )}
                    <StyledWrapper {...styles.partNumbersSection}>
                        <XngZone
                            contentId={props.id}
                            normalWidth={true}
                            zoneName="Content6"
                            hint="Zone For Product Details Part Numbers"
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.actionsWrapper} {...{ productHasMultipleSaleQty }}>
                        <ProductPriceAndActions
                            showAddToCartActions={false}
                            isCustomSignInStyle={true}
                            showAddToList={false}
                            extendedStyles={styles.productPriceAndActionsStyle}
                        />
                        <XngZone
                            contentId={props.id}
                            zoneName="Content3"
                            hint="Zone For Actions (Price, Inventory, and availablity)"
                        />
                        <ProductPriceAndActions
                            showAvailability={false}
                            showPrice={false}
                            showMinimumOrderQty={false}
                            showMultipleSaleQty={false}
                        />
                        <XngZone
                            contentId={props.id}
                            zoneName="Content4"
                            hint="Zone For Actions (Add To List, Add To Cart, Qty, ...etc)"
                        />
                    </StyledWrapper>
                    <Typography variant="legend">{siteMessage("FLIP_Prices_In_USD_Message")}</Typography>
                    <XngZone
                        contentId={props.id}
                        zoneName="ActionsTop"
                        hint="Zone For ProductDetailsActionsTopSection"
                    />
                    <Typography {...styles.tariffTextStyle}>{siteMessage("FLIP_Tariff_Message")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomSection} data-test-selector="productDetails_bottomSection">
                <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Specifications" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect()(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
